import React, { useEffect, useState, useRef } from "react";
import { View } from "react-native";
import { useRouter } from "expo-router";
import {
  ContactCreateForm,
  ContactCreateFormRef,
  ContactPayload,
} from "@/components/screens/contact/ContactCreateForm";
import { CreateHeader } from "@/components/screens/shared";
import useToast from "@/hooks/useToast";
import useAuth from "@/hooks/useAuth";
import { contactService } from "@/services/contactService";
import { useRowSelectionContext } from "@/providers/RowSelectionProvider";
import Head from "expo-router/head";
import { getSaveBehavior } from "@/lib/saveSettings";

export default function NewContactPage() {
  const toast = useToast();
  const router = useRouter();
  const { tenantId } = useAuth();
  const { triggerRefresh } = useRowSelectionContext();
  const [localDraft, setLocalDraft] = useState<ContactPayload>();
  const [dirty, setDirty] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const formRef = useRef<ContactCreateFormRef>(null);

  const handleCreateContact = async (payload: ContactPayload) => {
    try {
      if (!tenantId) throw new Error("No tenant ID available");

      // Extract contact_clients before creating contact
      const { contact_clients, ...contactFields } = payload.contact;

      // Create contact (don't include contact_clients in DB payload)
      const contactData = {
        first_name: contactFields.first_name,
        last_name: contactFields.last_name,
        email_address: contactFields.email_address,
        phone_number: contactFields.phone_number,
        meta_data: contactFields.meta_data,
        tenant_id: tenantId,
      };
      const newContact = await contactService.addContact(contactData);

      // Sync contact_clients to link table if any selected
      if (newContact?.id && contact_clients && contact_clients.length > 0) {
        await contactService.syncContactClients(newContact.id, contact_clients);
      }

      toast.showToast({ title: "Contact created successfully", toastType: "success" });
      triggerRefresh("contact");

      // Handle save behavior setting
      const behavior = await getSaveBehavior("contact");
      switch (behavior) {
        case "view_created":
          router.setParams({ new: undefined, id: newContact?.id });
          break;
        case "create_another":
          handleReset();
          break;
        case "close":
        default:
          router.setParams({ new: undefined, panel: undefined });
          break;
      }
    } catch (err: any) {
      toast.showToast({
        title: "Failed to create contact",
        message: err.message,
        toastType: "error",
      });
    }
  };

  // Sync firstName and lastName with form state
  useEffect(() => {
    if (localDraft?.contact?.first_name !== undefined && localDraft.contact.first_name !== firstName) {
      setFirstName(localDraft.contact.first_name);
    }
    if (localDraft?.contact?.last_name !== undefined && localDraft.contact.last_name !== lastName) {
      setLastName(localDraft.contact.last_name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localDraft?.contact?.first_name, localDraft?.contact?.last_name]);

  const handleReset = () => {
    setFirstName("");
    setLastName("");
    formRef.current?.handleReset();
  };

  return (
    <>
      <Head>
        <title>NEXUS New Contact</title>
        <meta name="description" content="" />
      </Head>
      <View style={{ flex: 1 }}>
        <CreateHeader
          entityType="contact"
          useFirstLastName
          firstName={firstName}
          lastName={lastName}
          onFirstNameChange={(name) => {
            setFirstName(name);
            formRef.current?.updateFirstName(name);
          }}
          onLastNameChange={(name) => {
            setLastName(name);
            formRef.current?.updateLastName(name);
          }}
          dirty={dirty}
          onSave={async () => {
            await formRef.current?.handleSave();
          }}
          onCancel={handleReset}
        />
        <View style={{ flex: 1, padding: 16 }}>
          <ContactCreateForm
            ref={formRef}
            initialContact={{ first_name: firstName, last_name: lastName }}
            onChange={(current) => {
              setLocalDraft(current);
            }}
            onSave={handleCreateContact}
            onDirtyChange={setDirty}
          />
        </View>
      </View>
    </>
  );
}
